import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import ElipsisCell from "../../../../components/TableBox/Cells/EllipsisCell";
import ElipsisText from "../../../../components/ElipsisText";
import Link from "../../../../components/Link";
import Badge from "../../../../components/Badge";
import Button from "../../../../components/Button";
import { RowClassName, Modules, RouteParamsTypes, PermissionModulesEnum, PermissionActionsEnum, RoleCodes } from "../../../../enums";
import { TransactionStatus } from "../../../../interfaces/transaction.interface"
import { TableContext } from "../../../../contexts/TableContext";
import { AuthContext } from "../../../../contexts/AuthContext";
import { VALIDATION_FIELD_MAPPING, INVOICE_STATUS } from "../../../../utils/appConstant";
import { hasResourseAccess, formatDollarAmount } from "../../../../utils/methods";

interface RowProps {
  item: any;
  className?: RowClassName;
  onDownload: (id: string) => void;
  onRemove: (_id: string) => void;
}

const Row: React.FC<RowProps> = ({ item, className, onDownload, onRemove }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { headCells } = useContext(TableContext);
  const { permissions, isSystemAdmin, loggedInUser } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Invoices]: [PermissionActionsEnum.Update] }] });
  const hasViewAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Invoices]: [PermissionActionsEnum.View] }] });
  const hasDeleteAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Invoices]: [PermissionActionsEnum.Delete] }] });

  // A Sent invoice can only be deleted by an Admin or System Admin (see EVYA-1422) -
  // checked against the full roles list, not just role_code, since role_code only
  // reflects the user's first role and a user can hold more than one.
  const isAdminUser = isSystemAdmin === 'true' ||
    (Array.isArray(loggedInUser?.roles) && loggedInUser.roles.some((role: any) => role?.code === RoleCodes.admin));
  const canDeleteDraft = hasDeleteAccess && item.status === INVOICE_STATUS.draft;
  const canDeleteSent = item.status === INVOICE_STATUS.sent && isAdminUser;

  const handleEditClick = (id: string) => {
    navigate(`${Modules.invoices}${RouteParamsTypes.edit}/${id}`);
  };

  const handleViewClick = (id: string) => {
    navigate(`${Modules.invoices}${RouteParamsTypes.view}/${id}`);
  };

  const handlePreviewClick = (id: string) => {
    const routeParamsType = item.status !== INVOICE_STATUS.completed ? RouteParamsTypes.edit : RouteParamsTypes.view;
    navigate(`${Modules.invoices}${routeParamsType}/${id}`, { state: { tab: 'invoice' } });
  };

  const handlePaymentClick = (id: string) => {
    const routeParamsType = item.status !== INVOICE_STATUS.completed ? RouteParamsTypes.edit : RouteParamsTypes.view;
    navigate(`${Modules.invoices}${routeParamsType}/${id}`, { state: { tab: 'payments' } });
  };

  const handleDownloadClick = (id: string) => {
    onDownload(id);
  };

  const handleRemoveClick = (id: string) => {
    onRemove(id);
  };

  const getStatusVariant = (status: TransactionStatus) => {
    if (status === INVOICE_STATUS.reconciled) return "danger";
    else if (status === INVOICE_STATUS.draft) return "secondary";
    else if (status === INVOICE_STATUS.sent) return "warning";
    else if (status === INVOICE_STATUS.overdue) return "warning";
    else if (status === INVOICE_STATUS.completed) return "success";
    else return "info"
  }

  const propertyNode = (
    <div>
      <ElipsisText
        width="100%"
        text={item?.property?.name || "-"}
        className="h6 mb-1"
      />
      <ElipsisText width="100%" text={item?.property?.city_data?.name || '-'} />
    </div>
  )

  const customerNode = (
    <div>
      <ElipsisText
        width="100%"
        text={`${item?.customer?.primary_first_name || ''} ${item?.customer?.primary_last_name || ''}`}
      />
    </div>
  )

  const invoiceAmountNode = (
    <div className="fw-semibold d-flex align-items-center gap-2">
      <span>{item.formatted_invoice_amount ? formatDollarAmount(item.formatted_invoice_amount) : '-'}</span>
      {!item.reviewed && (
        <Tooltip
          title={t("reservations.list.pageTexts.table.reviewedBy", { reviewedBy: item.reviewer?.name || "-" })}
          arrow
          classes={{ tooltip: "tooltip" }}
        >
          <i className="ri-spam-line text-warning ri-20px" />
        </Tooltip>
      )}
    </div>
  );

  const commissionNode = (
    <div className="fw-semibold d-flex align-items-center gap-2">
      <span>{item.formatted_commission_amount ? formatDollarAmount(item.formatted_commission_amount) : '-'}</span>
    </div>
  );

  const amountDueNode = (
    <div className="fw-semibold d-flex align-items-center gap-2">
      <span>{item.formatted_amount_due ? formatDollarAmount(item.formatted_amount_due) : '-'}</span>
    </div>
  );

  const createdByNode = (
    <div className="d-flex flex-column">
      <ElipsisText
        width="100%"
        text={item?.creator?.name || ''}
        className="h6 mb-1"
      />
      <ElipsisText width="100%" text={item?.created_at || '-'} />
    </div>
  )

  const invoiceNoNode = (
    <div className="d-flex flex-column">
      <ElipsisText
        width="100%"
        text={item?.invoice_no ?? '-'}
        node={
          <div className="d-flex align-items-center gap-1">
            <Link
              text={item?.invoice_no ?? "-"}
              href={`${Modules.invoices}${item.status !== INVOICE_STATUS.completed ? RouteParamsTypes.edit : RouteParamsTypes.view}/${item?.id}`}
              onClick={() => item.status !== INVOICE_STATUS.completed ? handleEditClick(item?.id) : handleViewClick(item?.id)}
            />
            {item.is_verified && <i className="ri-verified-badge-fill text-success" />}
          </div>
        }
        className="h6 mb-1 text-decoration-underline hyperlink"
      />
      <ElipsisText width="100%" text={item?.month_name && item?.year ? `${item.month_name}-${item.year}` : '-'} />
    </div>
  )

  const actionsNode = (
    <div className="d-flex align-items-sm-center justify-content-sm-start">
      {
        hasEditAccess && (item.status !== INVOICE_STATUS.completed) && (
          <Button
            className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
            icon={<i className="ri-edit-box-line ri-20px"></i>}
            onClick={() => handleEditClick(item.id)}
          />
        )
      }

      {
        hasViewAccess && (item.status === INVOICE_STATUS.completed) && (
          <Button
            className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
            icon={<i className="ri-eye-line ri-20px"></i>}
            onClick={() => handleViewClick(item.id)}
          />
        )
      }
      <Tooltip
        title={t('invoices.addList.pageTexts.table.preview')}
        arrow
        classes={{
          tooltip: 'tooltip',
        }}
      >
        <i
          className="ri-file-pdf-2-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
          onClick={() => handlePreviewClick(item?.id)}
        ></i>
      </Tooltip>
      <Tooltip
        title={t('invoices.addList.pageTexts.table.payments')}
        arrow
        classes={{
          tooltip: 'tooltip',
        }}
      >
        <i
          className="ri-bank-card-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
          onClick={() => handlePaymentClick(item?.id)}
        ></i>
      </Tooltip>
      <Tooltip
        title={t('invoices.addList.pageTexts.table.download')}
        arrow
        classes={{
          tooltip: 'tooltip',
        }}
      >
        <i
          className="ri-download-2-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
          onClick={() => handleDownloadClick(item?.id)}
        ></i>
      </Tooltip>
      <Tooltip
        title={
          <>
            <div>{t('invoices.addList.pageTexts.table.createdBy')} {item?.creator?.name ?? '-'}</div>
            <div>{t('invoices.addList.pageTexts.table.createdAt')} {item.created_at || "-"}</div>
          </>
        }
        arrow
        classes={{
          tooltip: 'tooltip',
        }}
      >
        <i className="ri-timer-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"></i>
      </Tooltip>
      {
        (canDeleteDraft || canDeleteSent) && (
          <Button
            className="btn btn-sm btn-icon btn-text-danger waves-effect waves-light rounded-pill"
            icon={<i className="ri-delete-bin-line ri-20px"></i>}
            onClick={(e) => handleRemoveClick(item.id)}
            aria-label={t('invoices.addList.pageTexts.table.delete')}
          />
        )
      }
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.invoiceStatus) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={
                <Badge
                  text={item.status_name || "-"}
                  variant={getStatusVariant(item.status)}
                />
              }
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.invoiceNo) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={invoiceNoNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.customer) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={customerNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.property) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={propertyNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.invoiceDate) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item?.formatted_invoice_date ?? '-'}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.dueDate) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item?.formatted_due_date ?? '-'}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.amountDue) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={amountDueNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.commission) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={commissionNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.invoiceAmount) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={invoiceAmountNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.createdBy) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={createdByNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.statementNumber) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item?.manager_statement?.statement_number || '-'}
              node={item?.manager_statement ? (
                <Link
                  text={item.manager_statement.statement_number}
                  href={`${Modules.reconciliation}${RouteParamsTypes.managerStatement}/${item.manager_statement.id}`}
                  target="_blank"
                />
              ) : undefined}
              className={item?.manager_statement ? "text-decoration-underline hyperlink" : ""}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>{actionsNode}</td>
          );
        }

        return (<ElipsisCell key={headCell.id} width={headCell.width} text={item[headCell.id] ?? "-"} />);
      })}
    </tr>
  );
};

export default Row;
